import { useState, useEffect } from 'react';
import { getConditions, accepterConditions, relancerParent, getValidationParent, validerParent } from '../api';

/**
 * Notice d'information et conditions d'utilisation.
 *   mode « accepter »       adulte, première connexion ou nouvelle version
 *   mode « attente_parent » joueur de moins de 15 ans : son parent doit valider
 *   mode « parent »         le parent, arrivé par le lien reçu par email
 *   mode « lecture »        simple consultation, depuis le pied de page
 */
export default function ConditionsPage({ mode: modeRecu, token, etat, onAcceptees, onDeco, onFermer }) {
  // Le serveur renvoie l'état « a_accepter » : c'est le mode « accepter » de cet écran
  const mode = modeRecu === 'a_accepter' ? 'accepter' : modeRecu;
  const [doc, setDoc]       = useState(null);
  const [coche, setCoche]   = useState(false);
  const [envoi, setEnvoi]   = useState(false);
  const [fini, setFini]     = useState(false);
  const [erreur, setErreur] = useState(null);
  const [info, setInfo]     = useState(null);

  useEffect(() => {
    (mode === 'parent' ? getValidationParent(token) : getConditions())
      .then(d => { setDoc(d); if (d.deja) setFini(true); })
      .catch(e => setErreur(e.message));
  }, [mode, token]);

  async function valider() {
    setEnvoi(true); setErreur(null);
    try {
      if (mode === 'parent') { await validerParent(token, doc.version); setFini(true); }
      else { await accepterConditions(doc.version); onAcceptees?.(); }
    } catch (e) { setErreur(e.message); }
    finally { setEnvoi(false); }
  }

  async function relancer() {
    setEnvoi(true); setErreur(null);
    try { await relancerParent(); setInfo('Un nouveau lien vient d\'être envoyé.'); }
    catch (e) { setErreur(e.message); }
    finally { setEnvoi(false); }
  }

  const cadre = { padding:'28px 18px 40px', maxWidth:620, margin:'0 auto' };
  const logo = <img src="/img/logo-equipote.png" alt="Équipote" style={{ height:34, width:'auto', display:'block', margin:'0 auto 22px' }} />;

  if (erreur && !doc) return (
    <div style={{ ...cadre, textAlign:'center' }}>{logo}
      <div style={{ fontSize:34, marginBottom:10 }}>⚠️</div>
      <p style={{ color:'var(--muted)', fontSize:14 }}>{erreur}</p></div>);
  if (!doc) return <div style={{ ...cadre, textAlign:'center', color:'var(--muted)' }}>Chargement…</div>;

  if (fini) return (
    <div style={{ ...cadre, textAlign:'center' }}>{logo}
      <div style={{ fontSize:40, marginBottom:10 }}>✅</div>
      <h1 style={{ fontFamily:'Barlow Condensed', fontSize:24, fontWeight:800 }}>Merci</h1>
      <p style={{ color:'var(--muted)', fontSize:14, lineHeight:1.7, marginTop:10 }}>
        Le compte de {doc.enfant} est activé. {doc.enfant} peut se connecter à l'application de {doc.club}
        avec les accès que vous avez reçus par email — ou vous pouvez l'utiliser vous-même sur votre téléphone.
      </p>
      <a href="/" className="btn btn-primary" style={{ display:'inline-block', width:'auto', padding:'11px 22px', marginTop:20, textDecoration:'none' }}>
        Ouvrir l'application</a>
    </div>);

  const titre = { accepter: 'Avant de commencer', attente_parent: 'Encore une étape', lecture: 'Vos données personnelles',
                  parent: `Validation pour ${doc.enfant}` }[mode];

  return (
    <div style={cadre}>
      {logo}
      <h1 style={{ fontFamily:'Barlow Condensed', fontSize:26, fontWeight:800, textAlign:'center' }}>{titre}</h1>

      {mode === 'parent' && (
        <p style={{ color:'var(--muted)', fontSize:14, lineHeight:1.7, textAlign:'center', margin:'10px 0 0' }}>
          {doc.club} utilise Équipote pour organiser ses équipes. {doc.enfant} ayant moins de 15 ans,
          son compte ne sera utilisable qu'après votre validation.
        </p>)}
      {mode === 'attente_parent' && (
        <div style={{ background:'rgba(59,130,246,.10)', border:'1px solid rgba(59,130,246,.35)', borderRadius:10,
                      padding:'13px 15px', margin:'16px 0 0', fontSize:13.5, lineHeight:1.65, color:'#bfdbfe' }}>
          Comme tu as moins de 15 ans, c'est un de tes parents qui doit valider ces règles.
          Un email lui a été envoyé{etat?.email_parent ? <> à <b>{etat.email_parent}</b></> : ''}.
          Dès qu'il aura validé, tu pourras utiliser l'application.
        </div>)}

      {/* Résumé en langage simple : pour les enfants, et en réalité pour tout le monde */}
      <div className="card" style={{ margin:'18px 0 14px' }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:10 }}>
          En bref</div>
        {doc.resume.map((l, i) => (
          <div key={i} style={{ display:'flex', gap:9, fontSize:13.5, lineHeight:1.6, marginTop: i ? 7 : 0 }}>
            <span style={{ color:'var(--accent)', flexShrink:0 }}>•</span><span>{l}</span></div>))}
      </div>

      <details style={{ marginBottom:18 }} open={mode === 'lecture'}>
        <summary style={{ cursor:'pointer', fontSize:13, fontWeight:600, color:'var(--muted)', padding:'6px 0' }}>
          Lire la notice complète et les conditions d'utilisation</summary>
        <div style={{ marginTop:10 }}>
          {doc.sections.map(s => (
            <div key={s.titre} style={{ marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:5 }}>{s.titre}</div>
              {s.texte.map((p, i) => <p key={i} style={{ fontSize:12.5, color:'var(--muted)', lineHeight:1.65, margin:'0 0 6px' }}>{p}</p>)}
            </div>))}
          <div style={{ fontSize:11, color:'#5b6370' }}>Version {doc.version}</div>
        </div>
      </details>

      {(mode === 'accepter' || mode === 'parent' || mode === 'attente_parent') && (
        <div style={{ position:'sticky', bottom:0, zIndex:5, background:'var(--bg)', borderTop:'1px solid var(--border)',
                      margin:'8px -18px 0', padding:'14px 18px calc(14px + env(safe-area-inset-bottom, 0px))' }}>
        {(mode === 'accepter' || mode === 'parent') && (
          <>
            <label style={{ display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer', fontSize:13.5, lineHeight:1.55, marginBottom:16 }}>
              <input type="checkbox" checked={coche} onChange={e => setCoche(e.target.checked)}
                     style={{ width:19, height:19, flexShrink:0, marginTop:1, accentColor:'var(--accent)' }} />
              <span>{mode === 'parent'
                ? <>Je certifie être le <b>responsable légal</b> de {doc.enfant}, avoir pris connaissance de la notice
                    d'information et j'accepte les conditions d'utilisation pour son compte.</>
                : <>J'ai pris connaissance de la notice d'information et j'accepte les conditions d'utilisation.</>}</span>
            </label>
            <button className="btn btn-primary" disabled={!coche || envoi} onClick={valider} style={{ opacity: !coche || envoi ? .5 : 1 }}>
              {envoi ? 'Validation…' : 'Valider'}</button>
          </>)}

        {mode === 'attente_parent' && (
          <button className="btn btn-secondary" disabled={envoi} onClick={relancer}>
            {envoi ? 'Envoi…' : 'Renvoyer le lien à mon parent'}</button>)}

        </div>)}

      {mode === 'lecture' && onFermer && <button className="btn btn-secondary" onClick={onFermer}>Fermer</button>}

      {info && <div style={{ color:'#22c55e', fontSize:13, marginTop:12, textAlign:'center' }}>{info}</div>}
      {erreur && <div style={{ color:'var(--danger)', fontSize:13, marginTop:12, textAlign:'center' }}>{erreur}</div>}

      {onDeco && (mode === 'accepter' || mode === 'attente_parent') && (
        <button onClick={onDeco} style={{ display:'block', margin:'18px auto 0', background:'none', border:'none',
                                          color:'var(--muted)', fontSize:12.5, cursor:'pointer', textDecoration:'underline' }}>
          Se déconnecter</button>)}
    </div>
  );
}
